import React, { useState, useRef } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  TouchableOpacity,
  Platform,
  type LayoutRectangle,
} from "react-native";
import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { ContactModel, GroupModel, MemoryModel, MediaModel, ITagAssociationMeta, ITag, IDate } from "common247";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedToggle } from "@/components/ThemedToggle";
import { useThemeColor } from "@/hooks/useThemeColor";
import { BrandColors, Spacing } from "@/constants/ThemeColours";
import type { IPopoverAnchor } from "@/utils/popover-positioning";

// Platform Selectors
import { ContactSelector } from "@/components/contacts/ContactSelector";
import { GroupSelector } from "@/components/groups/GroupSelector";
import { MemorySelector } from "@/components/memory/MemorySelector";
import { MediaSelector } from "@/components/media/MediaSelector";
import { TagSelector, TagSelectorModal } from "@/components/tags/TagSelector";
import { DateSelector } from "@/components/date/DateSelector";
import { GoogleMapsLocationSelector, type ISelectedLocationResult } from "@/components/location/GoogleMapsLocationSelector";
import { ThemedTag } from "@/components/tags/ThemedTag";
import { TagListStrip } from "@/components/tags/TagListStrip";
import { ShareSelector } from "@/components/sharing/ShareSelector";
import { ShareRecipientSelector } from "@/components/sharing/ShareRecipientSelector";
import { ComponentRefTag } from "@/components/design/ComponentRefTag";
import { ShareModel } from "common247";

// Rich Demo Datasets for Design Sandbox
const DEMO_CONTACTS: ContactModel[] = [
  {
    id: "demo-contact-1",
    displayName: "Evelyn Vance",
    email: "evelyn.vance@archival-heritage.org",
    isActive: true,
    userRole: "owner",
    canShare: true,
    ownerId: "demo-user",
    createdAt: Date.parse("2026-08-10T12:00:00Z"),
  },
  {
    id: "demo-contact-2",
    displayName: "Alexandre Dumas",
    email: "alexandre.dumas@literature.org",
    isActive: true,
    userRole: "owner",
    canShare: true,
    ownerId: "demo-user",
    createdAt: Date.parse("2026-08-12T14:30:00Z"),
  },
  {
    id: "demo-contact-3",
    displayName: "Mara Solis",
    email: "mara.solis@fieldwork.org",
    isActive: true,
    userRole: "owner",
    canShare: true,
    ownerId: "demo-user",
    createdAt: Date.parse("2026-09-01T09:15:00Z"),
  },
  {
    id: "demo-contact-4",
    displayName: "Silas Thorne",
    email: "silas.thorne@pacificridge.edu",
    isActive: true,
    userRole: "owner",
    canShare: true,
    ownerId: "demo-user",
    createdAt: Date.parse("2026-09-15T18:00:00Z"),
  },
];

const DEMO_GROUPS: GroupModel[] = [
  {
    id: "demo-group-1",
    name: "Pacific Ridge Oral Archivists",
    description: "Collaborative research collective preserving 20th century oral histories.",
    ownerId: "demo-user",
    contactIds: ["demo-contact-1", "demo-contact-2"],
    userRole: "owner",
    canShare: true,
    createdAt: Date.parse("2026-08-10T12:00:00Z"),
  },
  {
    id: "demo-group-2",
    name: "Coastal Cartography Guild",
    description: "Maritime surveyors documenting historic navigational shoreline routes.",
    ownerId: "demo-user",
    contactIds: ["demo-contact-3"],
    userRole: "owner",
    canShare: true,
    createdAt: Date.parse("2026-08-20T11:00:00Z"),
  },
  {
    id: "demo-group-3",
    name: "Solstice Historical Society",
    description: "Regional preservation council for cultural folk celebrations.",
    ownerId: "demo-user",
    contactIds: ["demo-contact-1", "demo-contact-4"],
    userRole: "owner",
    canShare: true,
    createdAt: Date.parse("2026-09-05T16:20:00Z"),
  },
];

const DEMO_MEMORIES: MemoryModel[] = [
  {
    id: "demo-mem-1",
    title: "1974 Pacific Coast Highway Expedition",
    description: "Field voice recordings, route maps, and handwritten journal letters.",
    date: "1974-08-15",
    location: { text: "Big Sur, California" },
    ownerId: "demo-user",
    ownerDisplayName: "Mara Solis",
    userRole: "owner",
    canShare: true,
    mediaIds: ["demo-media-1", "demo-media-2"],
    createdAt: Date.parse("1974-08-15T10:00:00.000Z"),
  },
  {
    id: "demo-mem-2",
    title: "Big Sur Cliffside Solstice Gathering",
    description: "Lantern festival along Pfeiffer Beach with maritime archive team.",
    date: "1982-06-21",
    location: { text: "Pfeiffer Beach, California" },
    ownerId: "demo-user",
    ownerDisplayName: "Evelyn Vance",
    userRole: "owner",
    canShare: true,
    mediaIds: ["demo-media-2"],
    createdAt: Date.parse("1982-06-21T18:30:00.000Z"),
  },
  {
    id: "demo-mem-3",
    title: "Monterey Maritime Archives Field Survey",
    description: "Oral history capture of lighthouse keepers and sea chart drafts.",
    date: "1991-10-04",
    location: { text: "Point Pinos, California" },
    ownerId: "demo-user",
    ownerDisplayName: "Silas Thorne",
    userRole: "owner",
    canShare: true,
    mediaIds: [],
    createdAt: Date.parse("1991-10-04T14:00:00.000Z"),
  },
];

const DEMO_MEDIA: MediaModel[] = [
  {
    id: "demo-media-1",
    title: "Big Sur Coastal Fog",
    url: "https://images.unsplash.com/photo-1506744038136-46273834b3fb",
    mediaCategory: "image",
    ownerId: "demo-user",
    createdAt: Date.now() - 3600000,
  },
  {
    id: "demo-media-2",
    title: "Point Pinos Lighthouse Sunset",
    url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e",
    mediaCategory: "image",
    ownerId: "demo-user",
    createdAt: Date.now() - 7200000,
  },
];

const DEMO_TAGS: Record<string, ITagAssociationMeta> = {
  "tag-1": {
    tagId: "tag-1",
    label: "Pacific Coast",
    tagType: "general",
    addedAt: Date.now() - 86400000,
    addedBy: "demo-user",
  },
  "tag-2": {
    tagId: "tag-2",
    label: "Expedition 1974",
    tagType: "memory",
    addedAt: Date.now() - 43200000,
    addedBy: "demo-user",
  },
  "tag-3": {
    tagId: "tag-3",
    label: "Field Research",
    tagType: "contact",
    addedAt: Date.now() - 21600000,
    addedBy: "demo-user",
  },
};

const DEMO_SHARES: ShareModel[] = [
  {
    id: "demo-share-1",
    entityId: "demo-memory-1",
    entityType: "memory",
    targetId: "demo-contact-1",
    targetType: "contact",
    role: "editor",
    ownerId: "demo-user",
    createdAt: Date.parse("2026-08-11T10:00:00Z"),
  },
  {
    id: "demo-share-2",
    entityId: "demo-memory-1",
    entityType: "memory",
    targetId: "demo-group-1",
    targetType: "group",
    role: "viewer",
    ownerId: "demo-user",
    createdAt: Date.parse("2026-08-15T16:00:00Z"),
  },
];

/**
 * Design Sandbox Selectors & Modals Workbench Screen.
 * Provides interactive launch surfaces and inspector panels for all platform selection controls:
 * ContactSelector, GroupSelector, MemorySelector, MediaSelector, TagSelector, DateSelector, GoogleMapsLocationSelector,
 * ShareSelector, and TagListStrip. All selectors adhere to the Popup UX pattern.
 */
export default function DesignSelectorsScreen() {
  const tintColor = useThemeColor({}, "tint");
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textMuted = useThemeColor({}, "textMuted");

  // Anchor simulation mode (popover anchored to button vs centered modal)
  const [useAnchorPosition, setUseAnchorPosition] = useState(true);

  // Active Selector Modal States
  const [isContactSelectorVisible, setIsContactSelectorVisible] = useState(false);
  const [contactSelectorMulti, setContactSelectorMulti] = useState(false);
  const [isGroupSelectorVisible, setIsGroupSelectorVisible] = useState(false);
  const [groupSelectorMulti, setGroupSelectorMulti] = useState(false);
  const [isMemorySelectorVisible, setIsMemorySelectorVisible] = useState(false);
  const [memorySelectorMulti, setMemorySelectorMulti] = useState(false);
  const [isMediaSelectorVisible, setIsMediaSelectorVisible] = useState(false);
  const [mediaSelectorMulti, setMediaSelectorMulti] = useState(true);
  const [isTagSelectorVisible, setIsTagSelectorVisible] = useState(false);
  const [tagSelectorMulti, setTagSelectorMulti] = useState(false);
  const [isDateSelectorVisible, setIsDateSelectorVisible] = useState(false);
  const [dateSelectorType, setDateSelectorType] = useState<"Date" | "Range">("Date");
  const [dateSelectorValue, setDateSelectorValue] = useState<IDate | string | null>(null);
  const [isLocationModalVisible, setIsLocationModalVisible] = useState(false);
  const [isShareSelectorVisible, setIsShareSelectorVisible] = useState(false);
  const [isMultiSelectorShareVisible, setIsMultiSelectorShareVisible] = useState(false);

  // Trigger Anchor Refs and Coordinates
  const contactBtnRef = useRef<View>(null);
  const groupBtnRef = useRef<View>(null);
  const memoryBtnRef = useRef<View>(null);
  const mediaBtnRef = useRef<View>(null);
  const tagBtnRef = useRef<View>(null);
  const dateBtnRef = useRef<View>(null);
  const locationBtnRef = useRef<View>(null);
  const shareBtnRef = useRef<View>(null);
  const multiShareBtnRef = useRef<View>(null);

  const [activeAnchor, setActiveAnchor] = useState<IPopoverAnchor | null>(null);

  // Selected Results State (Inspector Payloads)
  const [selectedContact, setSelectedContact] = useState<ContactModel | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<ContactModel[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<GroupModel | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<GroupModel[]>([]);
  const [selectedMemory, setSelectedMemory] = useState<MemoryModel | null>(null);
  const [selectedMemories, setSelectedMemories] = useState<MemoryModel[]>([]);
  const [memoryActionLog, setMemoryActionLog] = useState<string>("");
  const [selectedMediaItems, setSelectedMediaItems] = useState<MediaModel[]>([]);
  const [selectedTags, setSelectedTags] = useState<ITag[]>([]);
  const [activeTags, setActiveTags] = useState<Record<string, ITagAssociationMeta>>(DEMO_TAGS);
  const [selectedDatePayload, setSelectedDatePayload] = useState<{ payload?: IDate; text?: string } | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<ISelectedLocationResult | null>(null);
  const [activeShares, setActiveShares] = useState<ShareModel[]>(DEMO_SHARES);
  const [multiSelectorShareResult, setMultiSelectorShareResult] = useState<string | null>(null);

  // Helper to resolve button anchor bounds before opening picker
  const openWithAnchor = (
    ref: React.RefObject<View | null>,
    openModalFn: () => void
  ) => {
    if (!useAnchorPosition || !ref.current) {
      setActiveAnchor(null);
      openModalFn();
      return;
    }

    if (Platform.OS === "web") {
      const node = ref.current as unknown as HTMLElement;
      if (node && typeof node.getBoundingClientRect === "function") {
        const rect = node.getBoundingClientRect();
        setActiveAnchor({
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        });
        openModalFn();
        return;
      }
    }

    ref.current.measureInWindow((x, y, width, height) => {
      setActiveAnchor({ x, y, width, height });
      openModalFn();
    });
  };

  return (
    <ThemedScreen
      title="Selectors & Modals"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/design" as any),
      }}
    >
      <ScrollView contentContainerStyle={styles.container}>
        {/* Header Overview */}
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 4 }}>
          PLATFORM SELECTION CONTROLS // INTERACTIVE WORKBENCH
        </ThemedText>
        <ThemedText style={{ fontSize: 13, color: textMuted, marginBottom: 16, lineHeight: 18 }}>
          Test and evaluate the primary contextual selection popups. Verify search filtering, single vs multi-select, popover coordinate anchoring, and entity association payloads.
        </ThemedText>

        {/* Global Controls Card: Anchor Positioning */}
        <ThemedPanel padding={12} elevation="low" style={{ marginBottom: 16 }}>
          <View style={styles.settingsRow}>
            <View style={{ flex: 1, marginRight: 12 }}>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 6, marginBottom: 2 }}>
                <ThemedText type="defaultSemiBold" style={{ fontSize: 13 }}>
                  Popover Anchor Positioning
                </ThemedText>
                <ComponentRefTag name="REF: <ThemedPopupScreen:anchorPosition>" />
              </View>
              <ThemedText style={{ fontSize: 12, color: textMuted }}>
                {useAnchorPosition
                  ? "Selectors anchor contextually near the trigger element on web/desktop."
                  : "Selectors render as centered dialogs with screen backdrop."}
              </ThemedText>
            </View>
            <ThemedToggle
              value={useAnchorPosition}
              onValueChange={setUseAnchorPosition}
              activeLabel="Anchored"
              inactiveLabel="Centered"
            />
          </View>
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* 1. ContactSelector Showcase */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleGroup}>
              <View style={styles.iconCircle}>
                <Ionicons name="person-circle-outline" size={20} color={tintColor} />
              </View>
              <View>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  ContactSelector
                </ThemedText>
                <ThemedText style={styles.sectionSubtitle}>
                  Contextual popover contact selector with real-time search and multi-select.
                </ThemedText>
              </View>
            </View>
            <ComponentRefTag name="REF: <ContactSelector>" />
          </View>

          <View style={styles.actionButtonsRow}>
            <View ref={contactBtnRef} collapsable={false}>
              <ThemedButton
                title="Pick Single Contact"
                icon="person-add-outline"
                variant="primary"
                onPress={() => {
                  setContactSelectorMulti(false);
                  openWithAnchor(contactBtnRef, () => setIsContactSelectorVisible(true));
                }}
              />
            </View>
            <ThemedButton
              title="Pick Multiple Contacts"
              icon="people-outline"
              variant="secondary"
              onPress={() => {
                setContactSelectorMulti(true);
                openWithAnchor(contactBtnRef, () => setIsContactSelectorVisible(true));
              }}
            />
          </View>

          {/* Inspector Output */}
          <View style={[styles.inspectorBox, { backgroundColor: cardBg, borderColor }]}>
            <ThemedText style={styles.inspectorLabel}>Selected Contact Output:</ThemedText>
            {contactSelectorMulti ? (
              selectedContacts.length > 0 ? (
                <View style={styles.chipResultsRow}>
                  {selectedContacts.map((c) => (
                    <View key={c.id} style={[styles.resultPill, { borderColor: tintColor }]}>
                      <Ionicons name="person" size={12} color={tintColor} />
                      <ThemedText style={styles.resultPillText}>
                        {c.displayName} ({c.email || "No email"})
                      </ThemedText>
                    </View>
                  ))}
                </View>
              ) : (
                <ThemedText style={styles.inspectorPlaceholder}>No contacts selected yet.</ThemedText>
              )
            ) : selectedContact ? (
              <ThemedText style={styles.inspectorValue}>
                {selectedContact.displayName} • {selectedContact.email || "No email"} (ID: {selectedContact.id})
              </ThemedText>
            ) : (
              <ThemedText style={styles.inspectorPlaceholder}>No contact selected yet.</ThemedText>
            )}
          </View>
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* 2. GroupSelector Showcase */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleGroup}>
              <View style={styles.iconCircle}>
                <Ionicons name="people-outline" size={20} color={tintColor} />
              </View>
              <View>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  GroupSelector
                </ThemedText>
                <ThemedText style={styles.sectionSubtitle}>
                  Searchable group selector supporting single/multi selection and member tags.
                </ThemedText>
              </View>
            </View>
            <ComponentRefTag name="REF: <GroupSelector>" />
          </View>

          <View style={styles.actionButtonsRow}>
            <View ref={groupBtnRef} collapsable={false}>
              <ThemedButton
                title="Pick Single Group"
                icon="folder-outline"
                variant="primary"
                onPress={() => {
                  setGroupSelectorMulti(false);
                  openWithAnchor(groupBtnRef, () => setIsGroupSelectorVisible(true));
                }}
              />
            </View>
            <ThemedButton
              title="Pick Multiple Groups"
              icon="layers-outline"
              variant="secondary"
              onPress={() => {
                setGroupSelectorMulti(true);
                openWithAnchor(groupBtnRef, () => setIsGroupSelectorVisible(true));
              }}
            />
          </View>

          {/* Inspector Output */}
          <View style={[styles.inspectorBox, { backgroundColor: cardBg, borderColor }]}>
            <ThemedText style={styles.inspectorLabel}>Selected Group Output:</ThemedText>
            {groupSelectorMulti ? (
              selectedGroups.length > 0 ? (
                <View style={styles.chipResultsRow}>
                  {selectedGroups.map((g) => (
                    <View key={g.id} style={[styles.resultPill, { borderColor: tintColor }]}>
                      <Ionicons name="people" size={12} color={tintColor} />
                      <ThemedText style={styles.resultPillText}>
                        {g.name} ({g.contactIds.length} members)
                      </ThemedText>
                    </View>
                  ))}
                </View>
              ) : (
                <ThemedText style={styles.inspectorPlaceholder}>No groups selected yet.</ThemedText>
              )
            ) : selectedGroup ? (
              <ThemedText style={styles.inspectorValue}>
                {selectedGroup.name} • {selectedGroup.description || "No description"} (ID: {selectedGroup.id})
              </ThemedText>
            ) : (
              <ThemedText style={styles.inspectorPlaceholder}>No group selected yet.</ThemedText>
            )}
          </View>
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* 3. MemorySelector Showcase */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleGroup}>
              <View style={styles.iconCircle}>
                <Ionicons name="book-outline" size={20} color={tintColor} />
              </View>
              <View>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  MemorySelector
                </ThemedText>
                <ThemedText style={styles.sectionSubtitle}>
                  Contextual memory selector supporting single assignment or multi-memory selection with checkmarks.
                </ThemedText>
              </View>
            </View>
            <ComponentRefTag name="REF: <MemorySelector>" />
          </View>

          <View style={styles.actionButtonsRow}>
            <View ref={memoryBtnRef} collapsable={false}>
              <ThemedButton
                title="Pick Single Memory"
                icon="link-outline"
                variant="primary"
                onPress={() => {
                  setMemorySelectorMulti(false);
                  openWithAnchor(memoryBtnRef, () => setIsMemorySelectorVisible(true));
                }}
              />
            </View>
            <ThemedButton
              title="Pick Multiple Memories"
              icon="bookmarks-outline"
              variant="secondary"
              onPress={() => {
                setMemorySelectorMulti(true);
                openWithAnchor(memoryBtnRef, () => setIsMemorySelectorVisible(true));
              }}
            />
          </View>

          {/* Inspector Output */}
          <View style={[styles.inspectorBox, { backgroundColor: cardBg, borderColor }]}>
            <ThemedText style={styles.inspectorLabel}>Selected Memory Output:</ThemedText>
            {memorySelectorMulti ? (
              selectedMemories.length > 0 ? (
                <View style={styles.chipResultsRow}>
                  {selectedMemories.map((m) => (
                    <View key={m.id} style={[styles.resultPill, { borderColor: tintColor }]}>
                      <Ionicons name="book" size={12} color={tintColor} />
                      <ThemedText style={styles.resultPillText}>
                        {m.title} ({m.date || "No date"})
                      </ThemedText>
                    </View>
                  ))}
                </View>
              ) : (
                <ThemedText style={styles.inspectorPlaceholder}>No memories selected yet.</ThemedText>
              )
            ) : selectedMemory ? (
              <ThemedText style={styles.inspectorValue}>
                {selectedMemory.title} • Date: {selectedMemory.date || "None"} (ID: {selectedMemory.id})
              </ThemedText>
            ) : memoryActionLog ? (
              <ThemedText style={[styles.inspectorValue, { color: BrandColors.sungoldOrange.hex }]}>
                {memoryActionLog}
              </ThemedText>
            ) : (
              <ThemedText style={styles.inspectorPlaceholder}>No memory chosen yet.</ThemedText>
            )}
          </View>
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* 4. MediaSelector Showcase */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleGroup}>
              <View style={styles.iconCircle}>
                <Ionicons name="images-outline" size={20} color={tintColor} />
              </View>
              <View>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  MediaSelector
                </ThemedText>
                <ThemedText style={styles.sectionSubtitle}>
                  Contextual media gallery selector supporting single and multi-selection with responsive popover layout.
                </ThemedText>
              </View>
            </View>
            <ComponentRefTag name="REF: <MediaSelector>" />
          </View>

          <View style={styles.actionButtonsRow}>
            <View ref={mediaBtnRef} collapsable={false}>
              <ThemedButton
                title="Pick Multiple Media (Default)"
                icon="images-outline"
                variant="primary"
                onPress={() => {
                  setMediaSelectorMulti(true);
                  openWithAnchor(mediaBtnRef, () => setIsMediaSelectorVisible(true));
                }}
              />
            </View>
            <ThemedButton
              title="Pick Single Media"
              icon="image-outline"
              variant="secondary"
              onPress={() => {
                setMediaSelectorMulti(false);
                openWithAnchor(mediaBtnRef, () => setIsMediaSelectorVisible(true));
              }}
            />
          </View>

          {/* Inspector Output */}
          <View style={[styles.inspectorBox, { backgroundColor: cardBg, borderColor }]}>
            <ThemedText style={styles.inspectorLabel}>Selected Media Items ({selectedMediaItems.length}):</ThemedText>
            {selectedMediaItems.length > 0 ? (
              <View style={styles.chipResultsRow}>
                {selectedMediaItems.map((m) => (
                  <View key={m.id} style={[styles.resultPill, { borderColor: tintColor }]}>
                    <Ionicons name="image-outline" size={12} color={tintColor} />
                    <ThemedText style={styles.resultPillText}>
                      {m.title || m.id} ({m.type || "media"})
                    </ThemedText>
                  </View>
                ))}
              </View>
            ) : (
              <ThemedText style={styles.inspectorPlaceholder}>No media items selected yet.</ThemedText>
            )}
          </View>
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* 5. TagSelector Showcase */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleGroup}>
              <View style={styles.iconCircle}>
                <Ionicons name="pricetags-outline" size={20} color={tintColor} />
              </View>
              <View>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  TagSelector
                </ThemedText>
                <ThemedText style={styles.sectionSubtitle}>
                  Categorized entity tag selector with General, Contact, Group, Memory, and Media filters.
                </ThemedText>
              </View>
            </View>
            <ComponentRefTag name="REF: <TagSelector>" />
          </View>

          <View style={styles.actionButtonsRow}>
            <View ref={tagBtnRef} collapsable={false}>
              <ThemedButton
                title="Add Tag (Single Attach)"
                icon="pricetag-outline"
                variant="primary"
                onPress={() => {
                  setTagSelectorMulti(false);
                  openWithAnchor(tagBtnRef, () => setIsTagSelectorVisible(true));
                }}
              />
            </View>
            <ThemedButton
              title="Select Multiple Tags"
              icon="pricetags-outline"
              variant="secondary"
              onPress={() => {
                setTagSelectorMulti(true);
                openWithAnchor(tagBtnRef, () => setIsTagSelectorVisible(true));
              }}
            />
          </View>

          {/* Inspector for Multi-select Tags */}
          {tagSelectorMulti && (
            <View style={[styles.inspectorBox, { backgroundColor: cardBg, borderColor, marginTop: 10 }]}>
              <ThemedText style={styles.inspectorLabel}>Selected Tags ({selectedTags.length}):</ThemedText>
              {selectedTags.length > 0 ? (
                <View style={styles.chipResultsRow}>
                  {selectedTags.map((t) => (
                    <View key={t.id} style={[styles.resultPill, { borderColor: tintColor }]}>
                      <Ionicons name="pricetag" size={12} color={tintColor} />
                      <ThemedText style={styles.resultPillText}>
                        {t.value} ({t.tagType})
                      </ThemedText>
                    </View>
                  ))}
                </View>
              ) : (
                <ThemedText style={styles.inspectorPlaceholder}>No tags selected yet.</ThemedText>
              )}
            </View>
          )}

          {/* TagListStrip Component Demonstration */}
          <View style={{ marginTop: 12 }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <ThemedText type="defaultSemiBold" style={{ fontSize: 13 }}>
                TagListStrip Specimen
              </ThemedText>
              <ComponentRefTag name="REF: <TagListStrip>" />
            </View>
            <TagListStrip
              entityId="demo-entity-1"
              entityType="memory"
              tags={activeTags}
              canEdit={true}
              isOwner={true}
              onTagAdded={(assoc) => {
                setActiveTags((prev) => ({ ...prev, [assoc.tagId]: assoc }));
              }}
              onTagRemoved={(tagId) => {
                setActiveTags((prev) => {
                  const next = { ...prev };
                  delete next[tagId];
                  return next;
                });
              }}
            />
          </View>
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* 6. DateSelector (Single Date & Range) Showcase */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleGroup}>
              <View style={styles.iconCircle}>
                <Ionicons name="calendar-outline" size={20} color={tintColor} />
              </View>
              <View>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  DateSelector Modal
                </ThemedText>
                <ThemedText style={styles.sectionSubtitle}>
                  Supports single dates, approximate (~), month/year, and full multi-day date ranges.
                </ThemedText>
              </View>
            </View>
            <ComponentRefTag name="REF: <DateSelector>" />
          </View>

          <View style={styles.actionButtonsRow}>
            <View ref={dateBtnRef} collapsable={false}>
              <ThemedButton
                title="Pick Single Date"
                icon="calendar"
                variant="primary"
                onPress={() => {
                  setDateSelectorType("Date");
                  setDateSelectorValue(null);
                  openWithAnchor(dateBtnRef, () => setIsDateSelectorVisible(true));
                }}
              />
            </View>
            <ThemedButton
              title="Pick Date Range"
              icon="calendar-outline"
              variant="secondary"
              onPress={() => {
                setDateSelectorType("Range");
                setDateSelectorValue(null);
                openWithAnchor(dateBtnRef, () => setIsDateSelectorVisible(true));
              }}
            />
            <ThemedButton
              title="Range with Duration"
              icon="time-outline"
              variant="outline"
              onPress={() => {
                setDateSelectorType("Range");
                setDateSelectorValue({
                  type: "range",
                  from: {
                    precision: "exact_datetime",
                    year: 2026,
                    month: 10,
                    day: 18,
                    time: "14:00",
                    dateStr: "18-Oct-2026 14:00",
                  },
                  duration: "2 hours",
                  formatted: "18-Oct-2026 14:00 (2 hours)",
                });
                openWithAnchor(dateBtnRef, () => setIsDateSelectorVisible(true));
              }}
            />
          </View>

          {/* Inspector Output */}
          <View style={[styles.inspectorBox, { backgroundColor: cardBg, borderColor }]}>
            <ThemedText style={styles.inspectorLabel}>Selected Date Output:</ThemedText>
            {selectedDatePayload ? (
              <View style={{ gap: 2 }}>
                <ThemedText style={styles.inspectorValue}>
                  {selectedDatePayload.text || "Selected"} • Type: {selectedDatePayload.payload?.type || "date"}
                </ThemedText>
                {selectedDatePayload.payload?.duration && (
                  <ThemedText style={[styles.inspectorValue, { color: tintColor }]}>
                    Duration: {selectedDatePayload.payload.duration} • Calculated End: {selectedDatePayload.payload.to?.dateStr || "N/A"}
                  </ThemedText>
                )}
              </View>
            ) : (
              <ThemedText style={styles.inspectorPlaceholder}>No date picked yet.</ThemedText>
            )}
          </View>
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* 7. GoogleMapsLocationSelector Showcase */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleGroup}>
              <View style={styles.iconCircle}>
                <Ionicons name="location-outline" size={20} color={tintColor} />
              </View>
              <View>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  GoogleMapsLocationSelector
                </ThemedText>
                <ThemedText style={styles.sectionSubtitle}>
                  Interactive location selector with OpenStreetMap search, coordinate entry, and map embeds (also exported as LocationSelector).
                </ThemedText>
              </View>
            </View>
            <ComponentRefTag name="REF: <GoogleMapsLocationSelector>" />
          </View>

          <View style={styles.actionButtonsRow}>
            <View ref={locationBtnRef} collapsable={false}>
              <ThemedButton
                title="Pick Geographical Location"
                icon="location"
                variant="primary"
                onPress={() => openWithAnchor(locationBtnRef, () => setIsLocationModalVisible(true))}
              />
            </View>
          </View>

          {/* Inspector Output */}
          <View style={[styles.inspectorBox, { backgroundColor: cardBg, borderColor }]}>
            <ThemedText style={styles.inspectorLabel}>Selected Location Output:</ThemedText>
            {selectedLocation ? (
              <ThemedText style={styles.inspectorValue}>
                {selectedLocation.location} • ({selectedLocation.latitude.toFixed(4)}, {selectedLocation.longitude.toFixed(4)})
                {selectedLocation.isApproximate ? " • ~ Approximate" : ""}
              </ThemedText>
            ) : (
              <ThemedText style={styles.inspectorPlaceholder}>No location selected yet.</ThemedText>
            )}
          </View>
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* 8. ShareSelector Showcase */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleGroup}>
              <View style={styles.iconCircle}>
                <Ionicons name="share-social-outline" size={20} color={tintColor} />
              </View>
              <View>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  ShareSelector Popup
                </ThemedText>
                <ThemedText style={styles.sectionSubtitle}>
                  Contextual share management popover adhering to the Popup UX pattern.
                </ThemedText>
              </View>
            </View>
            <ComponentRefTag name="REF: <ShareSelector>" />
          </View>

          <View style={styles.actionButtonsRow}>
            <View ref={shareBtnRef} collapsable={false}>
              <ThemedButton
                title="Manage & Pick Shares"
                icon="share-social"
                variant="primary"
                onPress={() => openWithAnchor(shareBtnRef, () => setIsShareSelectorVisible(true))}
              />
            </View>
          </View>

          {/* Inspector Output */}
          <View style={[styles.inspectorBox, { backgroundColor: cardBg, borderColor }]}>
            <ThemedText style={styles.inspectorLabel}>Active Shares Payload:</ThemedText>
            <ThemedText style={styles.inspectorValue}>
              {activeShares.length} Active {activeShares.length === 1 ? "Share" : "Shares"} •{" "}
              {activeShares.map((s) => `${s.targetType}:${s.role}`).join(", ")}
            </ThemedText>
          </View>
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* 9. ShareRecipientSelector (Batch Mode) Showcase */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleGroup}>
              <View style={styles.iconCircle}>
                <Ionicons name="people-outline" size={20} color={tintColor} />
              </View>
              <View>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  ShareRecipientSelector Dialog
                </ThemedText>
                <ThemedText style={styles.sectionSubtitle}>
                  Bulk sharing dialog for multi-item selections across Media, Memories, Contacts, and Groups.
                </ThemedText>
              </View>
            </View>
            <ComponentRefTag name="REF: <ShareRecipientSelector>" />
          </View>

          <View style={styles.actionButtonsRow}>
            <View ref={multiShareBtnRef} collapsable={false}>
              <ThemedButton
                title="Launch Multi-Item Share (5 Items)"
                icon="share-outline"
                variant="primary"
                onPress={() => setIsMultiSelectorShareVisible(true)}
              />
            </View>
          </View>

          {/* Inspector Output */}
          <View style={[styles.inspectorBox, { backgroundColor: cardBg, borderColor }]}>
            <ThemedText style={styles.inspectorLabel}>Batch Share Result:</ThemedText>
            {multiSelectorShareResult ? (
              <ThemedText style={styles.inspectorValue}>{multiSelectorShareResult}</ThemedText>
            ) : (
              <ThemedText style={styles.inspectorPlaceholder}>No batch share confirmed yet.</ThemedText>
            )}
          </View>
        </ThemedPanel>



        {/* ========================================================================= */}
        {/* Modal Mounts */}
        {/* ========================================================================= */}
        {/* 1. ContactSelector Modal */}
        <ContactSelector
          visible={isContactSelectorVisible}
          onClose={() => setIsContactSelectorVisible(false)}
          anchorPosition={activeAnchor}
          multiSelect={contactSelectorMulti}
          contacts={DEMO_CONTACTS}
          selectedContactIds={contactSelectorMulti ? selectedContacts.map((c) => c.id) : selectedContact ? [selectedContact.id] : []}
          onSelectContact={(contact) => {
            setSelectedContact(contact);
            setIsContactSelectorVisible(false);
          }}
          onSelectMultipleContacts={(contacts) => {
            setSelectedContacts(contacts);
          }}
        />

        {/* 2. GroupSelector Modal */}
        <GroupSelector
          visible={isGroupSelectorVisible}
          onClose={() => setIsGroupSelectorVisible(false)}
          anchorPosition={activeAnchor}
          multiSelect={groupSelectorMulti}
          groups={DEMO_GROUPS}
          selectedGroupIds={groupSelectorMulti ? selectedGroups.map((g) => g.id) : selectedGroup ? [selectedGroup.id] : []}
          onSelectGroup={(group) => {
            setSelectedGroup(group);
            setIsGroupSelectorVisible(false);
          }}
          onSelectMultipleGroups={(groups) => {
            setSelectedGroups(groups);
          }}
        />

        {/* 3. MemorySelector Modal */}
        <MemorySelector
          visible={isMemorySelectorVisible}
          onClose={() => setIsMemorySelectorVisible(false)}
          anchorPosition={activeAnchor}
          multiSelect={memorySelectorMulti}
          selectedMemoryIds={memorySelectorMulti ? selectedMemories.map((m) => m.id!) : selectedMemory ? [selectedMemory.id!] : []}
          selectedCount={3}
          itemTypeLabel="media items"
          memories={DEMO_MEMORIES}
          onSelectMemory={(mem) => {
            setSelectedMemory(mem);
            setMemoryActionLog("");
            setIsMemorySelectorVisible(false);
          }}
          onSelectMultipleMemories={(mems) => {
            setSelectedMemories(mems);
          }}
          onCreateNewMemory={() => {
            setMemoryActionLog("+ Create new memory triggered from MemorySelector");
            setSelectedMemory(null);
            setIsMemorySelectorVisible(false);
          }}
        />

        {/* 4. MediaSelector Modal */}
        <MediaSelector
          visible={isMediaSelectorVisible}
          onClose={() => setIsMediaSelectorVisible(false)}
          anchorPosition={activeAnchor}
          multiSelect={mediaSelectorMulti}
          selectedIds={selectedMediaItems.map((m) => m.id)}
          onSelectMedia={(items) => {
            setSelectedMediaItems(items);
          }}
          onSelectSingleMedia={(item) => {
            setSelectedMediaItems([item]);
            setIsMediaSelectorVisible(false);
          }}
        />

        {/* 5. TagSelector Modal */}
        <TagSelectorModal
          visible={isTagSelectorVisible}
          onClose={() => setIsTagSelectorVisible(false)}
          anchorPosition={activeAnchor}
          multiSelect={tagSelectorMulti}
          selectedTagIds={selectedTags.map((t) => t.id)}
          entityType="memory"
          entityId="demo-entity-1"
          currentTags={activeTags}
          onTagAdded={(assoc) => {
            setActiveTags((prev) => ({ ...prev, [assoc.tagId]: assoc }));
            setIsTagSelectorVisible(false);
          }}
          onSelectMultipleTags={(tags) => {
            setSelectedTags(tags);
          }}
        />

        {/* 6. DateSelector Modal */}
        <DateSelector
          visible={isDateSelectorVisible}
          onClose={() => setIsDateSelectorVisible(false)}
          anchorPosition={activeAnchor}
          type={dateSelectorType}
          value={dateSelectorValue}
          onApply={(payload, jsonString) => {
            setSelectedDatePayload({ payload, text: jsonString });
            setIsDateSelectorVisible(false);
          }}
        />

        {/* 7. Location Selector Popup */}
        <GoogleMapsLocationSelector
          visible={isLocationModalVisible}
          onClose={() => setIsLocationModalVisible(false)}
          anchorPosition={activeAnchor}
          initialLocation={selectedLocation?.location}
          initialLatitude={selectedLocation?.latitude}
          initialLongitude={selectedLocation?.longitude}
          initialIsApproximate={selectedLocation?.isApproximate}
          onSelect={(result) => {
            setSelectedLocation(result);
            setIsLocationModalVisible(false);
          }}
        />

        {/* 8. ShareSelector Popup */}
        <ShareSelector
          visible={isShareSelectorVisible}
          onClose={() => setIsShareSelectorVisible(false)}
          anchorPosition={activeAnchor}
          itemTitle="Echoes of the Pacific Coastline"
          itemType="memory"
          shares={activeShares}
          contacts={DEMO_CONTACTS}
          groups={DEMO_GROUPS}
          onShare={(targetId, targetType, role) => {
            const newShare: ShareModel = {
              id: `share-${Date.now()}`,
              entityId: "demo-memory-1",
              entityType: "memory",
              targetId,
              targetType,
              role,
              ownerId: "demo-user",
              createdAt: Date.now(),
            };
            setActiveShares((prev) => [...prev, newShare]);
          }}
          onUnshare={(shareId) => {
            setActiveShares((prev) => prev.filter((s) => s.id !== shareId));
          }}
          testID="design-share-selector"
        />

        {/* 9. ShareRecipientSelector Modal (Batch Mode) */}
        <ShareRecipientSelector
          visible={isMultiSelectorShareVisible}
          onClose={() => setIsMultiSelectorShareVisible(false)}
          selectedCount={5}
          itemTypeLabel="items"
          contacts={DEMO_CONTACTS}
          groups={DEMO_GROUPS}
          onConfirmShare={(targetId, targetType, role) => {
            const targetName =
              targetType === "contact"
                ? DEMO_CONTACTS.find((c) => c.id === targetId)?.displayName || targetId
                : DEMO_GROUPS.find((g) => g.id === targetId)?.name || targetId;
            setMultiSelectorShareResult(`Shared 5 items with ${targetType} "${targetName}" as ${role}`);
            setIsMultiSelectorShareVisible(false);
          }}
        />

      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 12,
    paddingBottom: 40,
  },
  settingsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sectionPanel: {
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  sectionTitleGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flex: 1,
    marginRight: 8,
  },
  iconCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "rgba(0, 122, 255, 0.08)",
    alignItems: "center",
    justifyContent: "center",
  },
  sectionTitle: {
    fontSize: 15,
  },
  sectionSubtitle: {
    fontSize: 12,
    opacity: 0.65,
    marginTop: 2,
    lineHeight: 16,
  },
  actionButtonsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginVertical: 6,
  },
  inspectorBox: {
    borderWidth: 1,
    borderRadius: 6,
    padding: 10,
    marginTop: 10,
  },
  inspectorLabel: {
    fontSize: 11,
    fontWeight: "700",
    opacity: 0.6,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  inspectorValue: {
    fontSize: 13,
    fontWeight: "600",
  },
  inspectorPlaceholder: {
    fontSize: 12,
    fontStyle: "italic",
    opacity: 0.5,
  },
  chipResultsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 4,
  },
  resultPill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 3,
    backgroundColor: "rgba(0, 0, 0, 0.03)",
  },
  resultPillText: {
    fontSize: 11,
    fontWeight: "600",
  },
});
